import { LitElement, html, unsafeCSS, CSSResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import styles from './dialog.scss?inline';
import bodyStyles from './dialog.body.scss?inline';
import slotStyles from './dialog.slot.scss?inline';
import { appendStylesheet } from '../core/utils';
import { IconButtonComponent } from '../icon-button/icon-button';
import { IconComponent } from '../icon/icon';
import { iconService } from '../icon/icon.service';

IconComponent.define();
IconButtonComponent.define();
iconService.importStickersheet();

export type DialogType = 'modal' | 'non-modal';
export type DialogVariant =
  | 'basic'
  | 'success'
  | 'error'
  | 'warning'
  | 'drawer'
  | 'internal'
  | 'internal-read-aloud';
export type TabIndex = 0 | -1;

export class DialogComponent extends LitElement {
  static selector = `{{c11n-prefix}}-dialog`;
  static override styles: CSSResult = unsafeCSS(styles);
  static define = () => {
    const isDeclared = customElements.get(DialogComponent.selector);
    if (!isDeclared) {
      customElements.define(DialogComponent.selector, DialogComponent);
    }
  };

  // TODO: remove magic numbers
  // We need to be able to see at least a few lines of text for the scroll container to be usable
  // line-height for body copy is typically 24px, so 24 * 3 + 12 (padding top, 6, and bottom, 8,
  // of body content) = 86
  private _minBodyHeight: number = 86;

  beforeElIntersecting!: boolean;
  afterElIntersecting!: boolean;
  resizeObserver!: ResizeObserver;
  dialogHeight!: number;
  headerHeight!: number;
  bodyHeight!: number;
  footerHeight!: number;
  intersectionObserver!: IntersectionObserver;
  clickStartTarget!: EventTarget;

  @query('dialog.c11n-dialog')
  public dialogEl!: HTMLDialogElement;

  @query('.c11n-dialog__header')
  headerEl!: HTMLElement;

  @query('.c11n-dialog__body__content')
  bodyEl!: HTMLElement;

  @query('.c11n-dialog__footer')
  footerEl!: HTMLElement;

  @query('.c11n-dialog__body__content__before')
  beforeEl!: HTMLElement;

  @query('.c11n-dialog__body__content__after')
  afterEl!: HTMLElement;

  // Some attributes (type, variant) were being stripped in Stencil apps,
  // but we found a simple workaround. If using @property
  // we may need to differentiate the name from the attribute
  // https://github.com/ionic-team/stencil/issues/2703

  @property({ attribute: 'dialog-label', type: String })
  dialogLabel: string = 'Please provide a label';

  @property({ attribute: 'type', type: String })
  _type: DialogType = 'modal';

  @property({ attribute: 'variant', type: String })
  _variant: DialogVariant = 'basic';

  @property({ attribute: 'c11n-web-dialog', type: Boolean, reflect: true })
  readonly _c11n_web_dialog: boolean = true;

  @state()
  protected vh!: string;

  @state()
  protected bodyTabIndex: TabIndex = -1;

  @state()
  protected dialogTabIndex: TabIndex = -1;

  @state()
  protected dialogOpen: boolean = false;

  @state()
  protected autoMargin: boolean = false;

  @state()
  protected overflow: boolean = false;

  @state()
  protected fallbackLayout: boolean = false;

  handleTransitionEnd = () => {
    this.dialogEl.close();
    this.dialogEl.removeEventListener(
      'transitionend',
      this.handleTransitionEnd
    );
    document.body.classList.remove('c11n-dialog--modal-open');
    document.body.style.setProperty("--c11n-scrollbar-width", '0');
  };

  handleClickStart = (event: MouseEvent | TouchEvent) => {
    if (event.target) {
      this.clickStartTarget = event.target;
    }
  };

  handleCancelEvent = (event: Event) => {
    event.preventDefault();
  };

  handleBackdropClick = (event: PointerEvent) => {
    if (
      this._type === 'modal' &&
      event.target === this.dialogEl &&
      this.clickStartTarget === this.dialogEl
    ) {
      const rect = this.dialogEl.getBoundingClientRect();
      const isInDialog = (
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width
      );
      if (!isInDialog) {
        this.closeDialog();
      }
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this._type === 'modal') {
      this.closeDialog();
    }
  };

  handleWindowResize = () => {
    if (this.dialogEl.open) {
      this.vh = this.getVh(window.innerHeight);
      this.autoMargin = this.getAutoMargin(window.innerHeight);
      this.handleLayoutChanges();
    }
  };

  handleLayoutChanges = () => {
    this.overflow = this.getOverflow();
    this.fallbackLayout = this.getFallbackLayout();
    if (this.fallbackLayout) {
      this.bodyTabIndex = -1;
      this.dialogTabIndex = 0;
    } else {
      this.dialogTabIndex = -1;
      this.bodyTabIndex = this.overflow ? 0 : -1;
    }
  };

  getFallbackLayout = () => {
    // TODO: remove magic numbers
    // 22px is the difference between footer with conditional border and without
    const footerHeightDifference: number = 22;
    let bodyHeight: number = this._minBodyHeight;
    let footerHeight: number = this.footerHeight;
    if (this.overflow) {
      footerHeight += this.fallbackLayout ? footerHeightDifference : 0;
    } else {
      if (this.fallbackLayout) {
        if (!this.getIfOverflowOverridden()) {
          footerHeight += footerHeightDifference;
        }
      }
      if (this.getIfOverflowOverridden()) {
        bodyHeight = this.bodyHeight;
      }
    }
    return this.headerHeight + footerHeight + bodyHeight > this.dialogHeight;
  };

  getOverflow = () => {
    return (
      !(this.beforeElIntersecting && this.afterElIntersecting) &&
      !this.getIfOverflowOverridden()
    );
  };

  getIfOverflowOverridden = () => {
    return this.bodyHeight < this._minBodyHeight;
  };

  getVh = (viewportHeight: number) => {
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    // On mobile browsers, the value of vh is fixed based on the max height of the screen
    // hence, we can't get the full height of the screen when UI like search is in the view.
    // So, we're calculating the value of vh based on the actual height that is available
    // whether UI in the mobile browser is in the view or not.
    return `${viewportHeight * 0.01}px`;
  };

  getAutoMargin = (viewportHeight: number) => {
    const maxVerticalMargin: number = 128;
    return viewportHeight - maxVerticalMargin * 2 < this.dialogHeight;
  };

  setDialogHeight = (entry?: any) => {
    if (entry) {
      this.dialogHeight = entry.target.offsetHeight;
    } else {
      this.dialogHeight = this.dialogEl.offsetHeight;
    }
  };

  setHeaderHeight = (entry?: any) => {
    if (entry) {
      this.headerHeight = entry.target.offsetHeight;
    } else {
      this.headerHeight = this.headerEl.offsetHeight;
    }
  };

  setBodyHeight = (entry?: any) => {
    if (entry) {
      this.bodyHeight = entry.target.offsetHeight;
    } else {
      this.bodyHeight = this.bodyEl.offsetHeight;
    }
  };

  setFooterHeight = (entry?: any) => {
    if (entry) {
      this.footerHeight = entry.target.offsetHeight;
    } else {
      this.footerHeight = this.footerEl.offsetHeight;
    }
  };

  public openDialog = () => {
    if (this._type === 'modal') {
      /**
       * TODO:
       * Consider how class is added to document body from within the component
       * but the styles need to be global
       */

      // Needed to create the margin for if a scrollbar is present to offset the content that would otherwise shift and applied to the --c11n-scrollbar-width custom prop
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.classList.add('c11n-dialog--modal-open');
      document.body.style.setProperty("--c11n-scrollbar-width", `${scrollbarWidth}px`);
      this.vh = this.getVh(window.innerHeight);
      window.addEventListener('resize', this.handleWindowResize);
      document.addEventListener('keydown', this.handleKeyDown);
      this.dialogEl.showModal();
      /**
       * Wait to start transition
       */
      window.requestAnimationFrame(() => {
        this.dialogOpen = true;
        /**
         * Ensure hasAutoMargin and element heights are set after transition begins
         */
        window.requestAnimationFrame(() => {
          this.setDialogHeight();
          this.setHeaderHeight();
          this.setBodyHeight();
          this.setFooterHeight();
          this.autoMargin = this.getAutoMargin(window.innerHeight);
          this.handleLayoutChanges();
        });
      });
    } else {
      console.log('This is not a modal dialog');
    }
  };

  public closeDialog = () => {
    if (this._type === 'modal') {
      window.removeEventListener('resize', this.handleWindowResize);
      document.removeEventListener('keydown', this.handleKeyDown);
      this.dialogEl.addEventListener('transitionend', this.handleTransitionEnd);
      this.dialogOpen = false;
      this.clickStartTarget = null as unknown as EventTarget;
    }
  };

  addObservers = () => {
    this.resizeObserver = new ResizeObserver((entries) => {
      if (this.dialogEl.open) {
        for (const entry of entries) {
          switch (entry.target) {
            case this.dialogEl:
              this.setDialogHeight(entry);
              break;
            case this.headerEl:
              this.setHeaderHeight(entry);
              break;
            case this.bodyEl:
              this.setBodyHeight(entry);
              break;
            case this.footerEl:
              this.setFooterHeight(entry);
              this.handleLayoutChanges();
              break;
          }
        }
      }
    });
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (this.dialogEl.open) {
        for (const entry of entries) {
          switch (entry.target) {
            case this.beforeEl:
              this.beforeElIntersecting = entry.isIntersecting;
              break;
            case this.afterEl:
              this.afterElIntersecting = entry.isIntersecting;
              break;
          }
        }
        window.requestAnimationFrame(() => {
          this.handleLayoutChanges();
        });
      }
    });
    this.resizeObserver.observe(this.dialogEl);
    this.resizeObserver.observe(this.headerEl);
    this.resizeObserver.observe(this.bodyEl);
    this.resizeObserver.observe(this.footerEl);
    this.intersectionObserver.observe(this.beforeEl);
    this.intersectionObserver.observe(this.afterEl);
  };

  removeObservers = () => {
    this.resizeObserver.unobserve(this.dialogEl);
    this.resizeObserver.unobserve(this.headerEl);
    this.resizeObserver.unobserve(this.bodyEl);
    this.resizeObserver.unobserve(this.footerEl);
    this.intersectionObserver.unobserve(this.beforeEl);
    this.intersectionObserver.unobserve(this.afterEl);
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();

    setTimeout(() => {
      this.addObservers();
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    /**
     * removing listeners added in case dialog is open when disconnected
     */
    if (this.dialogEl.open) {
      this.dialogEl.close();
      document.body.classList.remove('c11n-dialog--modal-open');
      this.dialogOpen = false;
      document.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.removeObservers();
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles
      .toString()
      .replaceAll(/\bc11n-web-dialog\b/g, this.tagName.toLowerCase());
    appendStylesheet({
      target: this,
      styles: slotStylesheet,
      name: `${this.tagName.toLowerCase()}-slot`
    });
    appendStylesheet({
      target: document.body,
      styles: bodyStyles.toString(),
      name: `${this.tagName.toLowerCase()}-body`
    });
  }

  getIconMarkup = () => {
    if (this._variant !== 'basic') {
      if (this._variant === 'internal-read-aloud') {
        return html`<{{c11n-prefix}}-icon class="c11n-dialog__icon" name="chat-internal-fill" size="medium"></{{c11n-prefix}}-icon>`;
      }
      return html`<{{c11n-prefix}}-icon class="c11n-dialog__icon" name="${this._variant}-fill" size="medium" alt-text="${this._variant}"></{{c11n-prefix}}-icon>`;
    }
    return html``;
  };

  getHeader = () => {
    if (this._variant === 'internal-read-aloud') {
      return html`<h1
        class="c11n-dialog__header__content__internal-read-aloud-header"
      >
        Read aloud to the client
      </h1>`;
    }
    return html`<slot name="header"></slot>`;
  };

  getInternalBanner = () => {
    if (
      this._variant === 'internal' ||
      this._variant === 'internal-read-aloud'
    ) {
      return html`<div class="c11n-dialog__internal-banner">
        <p>Internal only</p>
      </div>`;
    }
    return html``;
  };

  override render() {
    const classes: any = {
      'c11n-dialog': true,
      'c11n-dialog--open': this.dialogOpen,
      'c11n-dialog--overflow': this.overflow,
      'c11n-dialog--auto-margin': this.autoMargin,
      'c11n-dialog--fallback-layout': this.fallbackLayout && this.dialogOpen
    };
    const classNames = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');

    return html`
    <dialog class="${classNames}" aria-label="${this.dialogLabel}" @click="${
      this.handleBackdropClick
    }" @mousedown="${this.handleClickStart}" @touchstart="${
      this.handleClickStart
    }" @cancel="${this.handleCancelEvent}" style="--vh: ${
      this.vh
    };" tabindex="${this.dialogTabIndex}">
    ${this.getInternalBanner()}
      <{{c11n-prefix}}-icon-button class="c11n-dialog__close-button" size="small" variant="ghost" @click="${
        this.closeDialog
      }">
        <button aria-label="Close modal dialog" title="Close modal dialog">
          <{{c11n-prefix}}-icon name="remove"></{{c11n-prefix}}-icon>
        </button>
      </{{c11n-prefix}}-icon-button>
      <header class="c11n-dialog__header">
        <div class="c11n-dialog__header__content">
          ${this.getHeader()}
        </div>
      </header>
      <div class="c11n-dialog__body" tabindex="${this.bodyTabIndex}">
        <div class="c11n-dialog__body__content">
          <div class="c11n-dialog__body__content__before"></div>
          <slot name="body"></slot>
          <div class="c11n-dialog__body__content__after"></div>
        </div>
      </div>
      <footer class="c11n-dialog__footer">
        <div class="c11n-dialog__footer__content">
          <slot name="footer"></slot>
        </div>
      </footer>
      ${this.getIconMarkup()}
      <div class="c11n-dialog__gradient-top"></div>
      <div class="c11n-dialog__gradient-bottom"></div>
    </dialog>
    `;
  }
}

export const C11nDialog = DialogComponent.selector;
